import * as React from "react";
import { needsStyledComponents } from "@sproutsocial/seeds-react-system-props";
import { Footer } from "./styles";
import { cn } from "./PanelShared";
import type { TypePanelFooterProps } from "./PanelTypes";

/**
 * Styled-components footer — the backward-compatible fallback. Routed to when
 * the consumer passes styled-system props (e.g. `<Panel.Footer bg="...">`).
 */
const PanelFooterStyled = ({ children, ...rest }: TypePanelFooterProps) => (
  <Footer p={450} data-panel-footer="" {...rest}>
    {children}
  </Footer>
);

/**
 * Tailwind/CSS-class footer — the preferred path. Renders a plain
 * `<div data-panel-footer>` with the `seeds-panel-footer` class from panel.css.
 *
 * `margin-bottom: var(--bleed, 0px)` lifts the footer above the Drawer's mobile
 * bottom-sheet bleed (see styles.ts). It is asserted by the jsdom tests and the
 * `--bleed` var is only set at runtime by the mobile Drawer, so it is applied
 * inline (falling back to 0px on desktop).
 */
const PanelFooterTailwind = ({ children, ...rest }: TypePanelFooterProps) => {
  const { className, style, ...domRest } = rest as {
    className?: string;
    style?: React.CSSProperties;
  } & Record<string, unknown>;

  return (
    <div
      className={cn("seeds-panel-footer", className)}
      style={{ marginBottom: "var(--bleed, 0px)", ...style }}
      data-panel-footer=""
      {...domRest}
    >
      {children}
    </div>
  );
};

/**
 * Hybrid footer router. Routes on the leftover props after the footer's own
 * props are removed.
 */
export const PanelFooter = (props: TypePanelFooterProps) => {
  const { children, ...rest } = props;

  if (needsStyledComponents(rest)) {
    return <PanelFooterStyled {...props} />;
  }

  return <PanelFooterTailwind {...props} />;
};

PanelFooter.displayName = "Panel.Footer";
